import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioSummary = () => {
  const portfolioData = {
    totalValue: 125750.50,
    dailyChange: 3250.75,
    dailyChangePercent: 2.65,
    weeklyChange: -1850.25,
    weeklyChangePercent: -1.45,
    totalInvested: 98500.00,
    totalProfit: 27250.50,
    profitPercent: 27.66
  };

  const holdings = [
    {
      symbol: 'BTC',
      name: 'Bitcoin',
      amount: 2.5,
      value: 113125.00,
      allocation: 90.0,
      change: 2.45,
      color: 'bg-orange-500'
    },
    {
      symbol: 'ETH',
      name: 'Ethereum',
      amount: 4.2,
      value: 11951.10,
      allocation: 9.5,
      change: -1.23,
      color: 'bg-blue-500'
    },
    {
      symbol: 'ADA',
      name: 'Cardano',
      amount: 1250,
      value: 606.25,
      allocation: 0.5,
      change: 1.89,
      color: 'bg-green-500'
    }
  ];

  const performanceMetrics = [
    { label: 'Best Performer', value: 'BTC (+2.45%)', icon: 'TrendingUp', color: 'text-success' },
    { label: 'Worst Performer', value: 'ETH (-1.23%)', icon: 'TrendingDown', color: 'text-error' },
    { label: 'Total Trades', value: '47', icon: 'Activity', color: 'text-primary' },
    { label: 'Win Rate', value: '68.1%', icon: 'Target', color: 'text-success' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const AllocationChart = () => (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          stroke="currentColor"
          strokeWidth="8"
          fill="transparent"
          className="text-muted"
        />
        {holdings?.map((holding, index) => {
          const circumference = 2 * Math.PI * 40;
          const strokeDasharray = `${(holding?.allocation / 100) * circumference} ${circumference}`;
          const strokeDashoffset = -holdings?.slice(0, index)?.reduce((acc, h) => acc + (h?.allocation / 100) * circumference, 0);
          
          return (
            <circle
              key={holding?.symbol}
              cx="50"
              cy="50"
              r="40"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className={holding?.symbol === 'BTC' ? 'text-orange-500' : holding?.symbol === 'ETH' ? 'text-blue-500' : 'text-green-500'}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs font-medium text-card-foreground">{formatCurrency(portfolioData?.totalValue)}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="PieChart" size={20} className="text-success" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Portfolio Summary</h2>
              <p className="text-sm text-muted-foreground">Your investment overview</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/portfolio-tracker'}>
            View Details
          </Button>
        </div>
      </div>
      <div className="p-6">
        {/* Total Value Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-2xl font-bold text-card-foreground">
              {formatCurrency(portfolioData?.totalValue)}
            </h3>
            <div className="flex items-center space-x-2">
              <Icon 
                name={portfolioData?.dailyChange > 0 ? 'ArrowUp' : 'ArrowDown'} 
                size={16} 
                className={portfolioData?.dailyChange > 0 ? 'text-success' : 'text-error'} 
              />
              <span className={`font-medium ${portfolioData?.dailyChange > 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(Math.abs(portfolioData?.dailyChange))} ({Math.abs(portfolioData?.dailyChangePercent)}%)
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Total Portfolio Value</p>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-sm font-medium text-card-foreground">Total Profit</span>
            </div>
            <p className="text-lg font-bold text-success">{formatCurrency(portfolioData?.totalProfit)}</p>
            <p className="text-xs text-muted-foreground">+{portfolioData?.profitPercent}% ROI</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="DollarSign" size={16} className="text-primary" />
              <span className="text-sm font-medium text-card-foreground">Invested</span>
            </div>
            <p className="text-lg font-bold text-card-foreground">{formatCurrency(portfolioData?.totalInvested)}</p>
            <p className="text-xs text-muted-foreground">Initial investment</p>
          </div>
        </div>

        {/* Holdings and Allocation */}
        <div className="flex items-start space-x-6 mb-6">
          <AllocationChart />
          <div className="flex-1 space-y-3">
            {holdings?.map((holding) => (
              <div key={holding?.symbol} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${holding?.color}`}></div>
                  <div>
                    <p className="text-sm font-medium text-card-foreground">{holding?.name}</p>
                    <p className="text-xs text-muted-foreground">{holding?.amount} {holding?.symbol}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-card-foreground">{formatCurrency(holding?.value)}</p>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-muted-foreground">{holding?.allocation}%</span>
                    <span className={`text-xs font-medium ${holding?.change > 0 ? 'text-success' : 'text-error'}`}>
                      {holding?.change > 0 ? '+' : ''}{holding?.change}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          {performanceMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${metric?.color === 'text-success' ? 'bg-success/10' : metric?.color === 'text-error' ? 'bg-error/10' : 'bg-primary/10'}`}>
                <Icon name={metric?.icon} size={16} className={metric?.color} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{metric?.label}</p>
                <p className="text-sm font-medium text-card-foreground">{metric?.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Last updated: {new Date()?.toLocaleTimeString()}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm">
              <Icon name="Download" size={16} />
              Export
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;